import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import ChoiceChip from '../../choice-chip';
import { CORRECT, getCharByElementPosition } from '../../../lib/exercise';
import MathContent from '../../math-content';
import Exercise from '../../exercise';
import { SingleFillBlanksCorrection } from '../fill-blanks';

const Container = styled.div`
    display: flex;
    flex-direction: column;
`;

const ExerciseContainer = styled.span`
    margin-left: 32px;
`;

const FeedbackContainer = styled.div`
    margin-left: 34px;

    ${(props) => props.theme.mediaQueries.desktop} {
        margin-left: 32px;
    }
`;

const Divider = styled.div`
    margin: 16px;
`;

export const Feedback = styled.span`
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0;
    margin-right: 48px;
    margin-top: 8px;
`;

const ChoiceChipContainer = styled.div``;

const Label = styled.span`
    font-weight: 600;
    margin-right: 4px;
`;

export const Correction = styled.div`
    display: flex;
    flex-direction: column;
`;

export const CorrectChoiceChip = styled.span`
    margin-top: 8px;
`;

const SingleFillChoiceContainer = styled.div``;

export const getLabel = (missedAnswer, correct, solution, feedback) => {
    if (solution) {
        if (feedback) {
            return 'Feedback specifico. ';
        }
        return '';
    }
    if (missedAnswer) {
        return 'Risposta non data. ';
    }
    return correct ? 'Risposta esatta. ' : 'Risposta sbagliata. ';
};

export const SingleFillChoiceCorrection = ({ id, correct, correctAnswer, feedback, index, missedAnswer, text, solution }) => {
    return (
        <SingleFillChoiceContainer>
            <ChoiceChipContainer>
                <ChoiceChip
                    badgeIndex={getCharByElementPosition(index + 1)}
                    handleClick={() => {}}
                    id={id}
                    key={`${index}`}
                    type="validated"
                    validationType={missedAnswer ? 'missing' : correct ? 'success' : 'error'}
                    value={text}
                />
            </ChoiceChipContainer>
            <FeedbackContainer>
                <Feedback>
                    <Label>{getLabel(missedAnswer, correct, solution, feedback)}</Label>
                    {feedback && <MathContent style={{ display: 'inline' }}>{feedback}</MathContent>}
                </Feedback>

                {!solution && !correct && !missedAnswer && (
                    <Correction>
                        <Label>{'La risposta esatta è:'}</Label>
                        <CorrectChoiceChip>
                            <ChoiceChip
                                disabled={true}
                                id={`${correctAnswer.id}`}
                                type="validated"
                                validationType="missing"
                                value={correctAnswer.text}
                            />
                        </CorrectChoiceChip>
                    </Correction>
                )}
            </FeedbackContainer>
        </SingleFillChoiceContainer>
    );
};

SingleFillChoiceCorrection.propTypes = {
    id: PropTypes.string,
    correct: PropTypes.bool,
    correctAnswer: PropTypes.object,
    feedback: PropTypes.string,
    index: PropTypes.number,
    missedAnswer: PropTypes.bool,
    solution: PropTypes.bool,
    text: PropTypes.string,
};

const FillChoiceCorrection = ({ exercise, answers, solution }) => {
    const selectedAnswers = answers ? answers.answer : [];
    return (
        <Container>
            <ExerciseContainer>
                <Exercise
                    answers={selectedAnswers}
                    exercise={{ ...exercise, showCorrection: true }}
                    homeworkTerminated={true}
                    showAttachments={false}
                    showSharedText={false}
                    showFeedbacks={false}
                    showActionsBar={false}
                    showTitle={false}
                    correctionStatus={{
                        exerciseId: exercise.id,
                        status: CORRECT,
                    }}
                    showHintBox={false}
                    solution={solution}
                />
            </ExerciseContainer>

            {exercise.options.map((option, index) => {
                const groupSelected = selectedAnswers.find((answer) => answer.gapId === option.id);
                const correctAnswer = option.values.find((el) => el.correct);
                const selectedAnswer = groupSelected
                    ? option.values.find((el) => `${el.id}` === groupSelected.optionId)
                    : option.values.find((el) => el.correct);
                const isMissedAnswer = !groupSelected;
                if (exercise.useSelect && !solution) {
                    return (
                        <Fragment key={option.id}>
                            <SingleFillBlanksCorrection
                                key={option.id}
                                id={`${option.id}`}
                                index={index}
                                missedAnswer={isMissedAnswer}
                                text={selectedAnswer.text}
                                correct={selectedAnswer.id === correctAnswer.id}
                                feedback={option.feedback}
                                correctAnswer={correctAnswer.text}
                                solution={solution}
                                parse={false}
                            />
                            {index < exercise.options.length - 1 && <Divider />}
                        </Fragment>
                    );
                }
                return (
                    <Fragment key={option.id}>
                        <SingleFillChoiceCorrection
                            id={option.id}
                            correct={selectedAnswer.correct}
                            correctAnswer={correctAnswer}
                            feedback={option.feedback}
                            index={index}
                            key={option.id}
                            missedAnswer={!groupSelected}
                            solution={solution}
                            text={selectedAnswer.text}
                        />
                        {index < exercise.options.length - 1 && <Divider />}
                    </Fragment>
                );
            })}
        </Container>
    );
};

FillChoiceCorrection.propTypes = {
    answers: PropTypes.shape({
        exerciseId: PropTypes.number,
        answer: PropTypes.array,
    }),
    exercise: PropTypes.object,
    solution: PropTypes.bool,
};

export default FillChoiceCorrection;
